import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import "./locationSearchInput.styles.scss";

function LocationSearchInput({ updateAddress }) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      locationBias: {
        north: 35.0,
        south: 30.4,
        west: -85.6,
        east: -80.8,
      },
      strictBounds: false,
      types: ["address"],
      componentRestrictions: { country: "us" },
    },
    debounce: 300,
  });

  const handleChange = (e) => {
    const raw = e.target.value;
    setInputValue(raw);
    setSelectedIndex(-1);
    // Append ", GA" behind the scenes to bias the Google Places query toward Georgia
    setValue(raw ? `${raw}, GA` : "");
    updateAddress(raw);
  };

  const handleSelect = (description) => {
    const cleanAddress = description.replace(", USA", "");
    setInputValue(cleanAddress);
    setValue(cleanAddress, false); // false avoids re-triggering prediction fetch
    clearSuggestions();
    setSelectedIndex(-1);
    updateAddress(cleanAddress);

    getGeocode({ address: description })
      .then(() => {
        // Successfully geocoded
      })
      .catch((error) => {
        console.error("Error geocoding address:", error);
        setInputValue("");
        setValue("");
        updateAddress("");
      });
  };

  const gaSuggestions =
    status === "OK"
      ? data.filter(
          (suggestion) =>
            suggestion.description.includes(", GA ") ||
            suggestion.description.includes(", GA,")
        )
      : [];

  const handleKeyDown = (e) => {
    if (gaSuggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < gaSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(gaSuggestions[selectedIndex].description);
    } else if (e.key === "Escape") {
      clearSuggestions();
      setSelectedIndex(-1);
    }
  };

  return (
    <div>
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          setTimeout(() => {
            clearSuggestions();
            setSelectedIndex(-1);
          }, 200);
        }}
        disabled={!ready}
        placeholder={t("enter_your_address_search")}
        className="address-input"
        autoComplete="off"
      />
      <div
        className="autocomplete-dropdown-container"
        style={{
          display: gaSuggestions.length > 0 ? "block" : "none",
        }}
      >
        {gaSuggestions.map((suggestion, i) => {
          const className =
            selectedIndex === i
              ? "suggestion-item suggestion-item--active clickable"
              : "suggestion-item clickable";

          return (
            <div
              key={suggestion.place_id || i}
              className={className}
              onMouseEnter={() => setSelectedIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(suggestion.description);
              }}
            >
              <span>{suggestion.description.replace(", USA", "")}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LocationSearchInput;
